import { FC, useRef, useEffect, useState, type UIEvent, useCallback } from 'react';
import classNames from 'classnames';
import { Icon } from 'common-components';
import { Autocomplete, TextField, Chip, SearchIcon } from '../../uiLibraryImports';
import Theme from '../theme';
import UseSearchDebounce from '../search/useSearchDebounce';
import Avatar from '../../avatar/avatar';
import { useTranslationContext } from '../../contexts/translationContext';
import { UserSearchProps } from './userSearchTypes';
import styles from './userSearch.module.scss';

const UserSearch: FC<UserSearchProps> = (props: UserSearchProps) => {
    const {
        users,
        selectedUsers = [],
        onChange,
        onSearchChange,
        fetchNextPage,
        isFetching,
        totalFetched,
        totalRows,
        usersFullName,
        listAbsolute = true
    } = props;
    const { translate } = useTranslationContext();
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const optionsRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const remainingOptionsRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const [oneLineHeight, setOneLineHeight] = useState<number | null>(null);
    const [limitTags, setLimitTags] = useState<number | null>(null);
    const [search, setSearch] = UseSearchDebounce();
    const [searchVal, setSearchVal] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isRemainingsOpen, setIsRemainingsOpen] = useState<boolean>(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [resetLimitTags, setResetLimitTags] = useState<boolean>(false);

    const onItemChange = (newValue: string) => {
        const selectedOptionsCpy = [...selectedUsers];
        if (selectedOptionsCpy.includes(newValue)) {
            selectedOptionsCpy.splice(selectedOptionsCpy.indexOf(newValue), 1);
        } else {
            selectedOptionsCpy.push(newValue);
        }
        onChange(selectedOptionsCpy);
        setSearchVal('');
    };

    useEffect(() => {
        onSearchChange((search as any) || '');
    }, [search]);

    useEffect(() => {
        setSearch?.((searchVal as any) || '');
    }, [searchVal]);

    useEffect(() => {
        if (!ref.current) return;
        const resizeObserver = new ResizeObserver(() => {
            const displayedOptions = limitTags !== null ? limitTags : selectedUsers.length;
            if (displayedOptions <= 1 && ref.current.clientHeight !== oneLineHeight && ref.current.clientHeight > 0) {
                setOneLineHeight(ref.current.clientHeight);
                return;
            }

            if (resetLimitTags) {
                setResetLimitTags(false);
                setLimitTags(null);
                return;
            }

            if (oneLineHeight && ref.current.clientHeight > oneLineHeight) {
                const newLimit = displayedOptions - 1;
                setLimitTags(newLimit);
            } else if (oneLineHeight === null && selectedUsers.length > 1) {
                setResetLimitTags(true);
                setLimitTags(1);
            } else if (limitTags !== null && selectedUsers.length <= limitTags) {
                setLimitTags(null);
            }
        });
        resizeObserver.observe(ref.current);
        // eslint-disable-next-line consistent-return
        return () => resizeObserver.disconnect();
    }, [oneLineHeight, limitTags, selectedUsers.length]);

    useEffect(() => {
        if (isOpen) setIsRemainingsOpen(false);
        const checkIfClickedOutside = (e: any) => {
            if (isOpen && !ref.current?.contains(e.target) && !optionsRef.current?.contains(e.target)) {
                setSearchVal('');
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isRemainingsOpen) setIsOpen(false);
        const checkIfClickedOutside = (e: any) => {
            if (isRemainingsOpen && !remainingOptionsRef.current?.contains(e.target)) {
                setIsRemainingsOpen(false);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [isRemainingsOpen]);

    useEffect(() => {
        if (optionsRef.current) {
            optionsRef.current.scrollTop = scrollPosition;
        }
    }, [scrollPosition, users]);

    const onInputChange = event => {
        setIsOpen(true);
        event && setSearchVal(event.target.value);
    };

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 20 &&
                    !isFetching &&
                    totalFetched &&
                    totalRows &&
                    totalFetched < totalRows
                ) {
                    setScrollPosition(scrollTop);
                    fetchNextPage?.();
                }
            }
        },
        [fetchNextPage, isFetching, totalFetched, totalRows]
    );

    useEffect(() => {
        fetchMoreOnBottomReached(optionsRef.current);
    }, [fetchMoreOnBottomReached]);

    return (
        <Theme>
            <div className={styles.wrapper}>
                <Autocomplete
                    multiple
                    options={users.map(option => option.email)}
                    value={selectedUsers}
                    onInputChange={onInputChange}
                    onChange={(_, newValue) => onChange(newValue)}
                    inputValue={searchVal}
                    renderInput={params => {
                        return (
                            <TextField
                                {...params}
                                onFocus={() => {
                                    !isRemainingsOpen && setIsOpen(true);
                                }}
                                variant="standard"
                                ref={ref}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {selectedUsers.length > 0 && (
                                                <Icon
                                                    onClick={() => onChange([])}
                                                    name="deleteEvent"
                                                    className={styles['delete-icon']}
                                                />
                                            )}
                                        </>
                                    ),
                                    startAdornment: (
                                        <div className={styles['start-wrapper']}>
                                            <SearchIcon fontSize="medium" className={styles['search-icon']} />
                                            {params.InputProps.startAdornment}
                                        </div>
                                    ),
                                    disableUnderline: true
                                }}
                                placeholder={selectedUsers.length === 0 ? translate('Search') : ''}
                                sx={{
                                    '& .MuiAutocomplete-inputRoot': {
                                        padding: '0 0.5rem !important',
                                        color: '#62626280'
                                    }
                                }}
                            />
                        );
                    }}
                    renderTags={value => {
                        const list = limitTags ? value.slice(0, limitTags) : value;
                        const chips = list.map((option, index) => (
                            <Chip
                                className={styles.chip}
                                key={index}
                                label={
                                    <div title={usersFullName[option]} className={styles.label}>
                                        {usersFullName[option]}
                                    </div>
                                }
                            />
                        ));
                        if (limitTags && value.length > limitTags) {
                            const remaining = value.slice(list.length);
                            chips.push(
                                <div onClick={() => setIsRemainingsOpen(true)} className={styles['expand-wrapper']}>
                                    <span>...+{remaining.length}</span>
                                    <Icon name="downArrowIcon" className={styles.expand} />
                                </div>
                            );
                        }
                        return chips;
                    }}
                    PopperComponent={() =>
                        isOpen && users.length > 0 ? (
                            <div
                                className={classNames(styles['option-wrapper'], {
                                    [styles['options-absolute']]: listAbsolute
                                })}
                            >
                                <div
                                    ref={optionsRef}
                                    className={styles.options}
                                    onScroll={(event: UIEvent<HTMLDivElement>) =>
                                        fetchMoreOnBottomReached(event.target as HTMLDivElement)
                                    }
                                >
                                    {users.map((option, i) => (
                                        <div
                                            className={classNames(styles.option, {
                                                [styles['option-selected']]: selectedUsers.includes(option.email)
                                            })}
                                            key={i}
                                            onClick={() => onItemChange(option.email)}
                                        >
                                            <Avatar
                                                size="2rem"
                                                name={usersFullName[option.email]}
                                                avatarSrc={option?.profilePicture}
                                            />
                                            <div title={option.email} className={styles.email}>
                                                {option.email}
                                            </div>
                                        </div>
                                    ))}
                                    {totalFetched && totalRows && totalFetched < totalRows && (
                                        <div className={styles['infinite-scroll-wrapper']}>
                                            <div className={styles['infinite-scroll']}>
                                                <Icon name="infiniteScroll" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null
                    }
                />
                {isRemainingsOpen && limitTags && !isOpen && (
                    <div className={styles['remaining-options-wrapper']}>
                        <div
                            ref={remainingOptionsRef}
                            className={classNames(styles['remaining-options'], {
                                [styles['remaining-options-absolute']]: listAbsolute
                            })}
                        >
                            {selectedUsers.slice(limitTags || 0).map((option, i) => (
                                <Chip
                                    className={classNames(styles.chip, styles['remaining-chip'])}
                                    key={i}
                                    label={
                                        <div
                                            title={usersFullName[option]}
                                            className={classNames(styles.label, styles['remaining-label'])}
                                        >
                                            {usersFullName[option]}
                                        </div>
                                    }
                                    onDelete={() => onItemChange(option)}
                                    deleteIcon={
                                        <span>
                                            <Icon name="grayClose" className={styles.close} />
                                        </span>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Theme>
    );
};

export default UserSearch;
